import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useQueryClient } from 'react-query'

import { useFile } from 'hooks/queries/files/useFile'
import { filesKeys } from 'hooks/queries/files/filesKeys'
import {
  optimisticRemoveFilesFromInfiniteLists,
  optimisticUpdateFileInInfiniteLists,
  setFileDetailCache,
} from 'hooks/queries/files/cacheUtils'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { AlmanacFileIndexItem, AlmanacFileShowItem } from 'hooks/queries/files/types'
import { CalendarFilters } from 'screens/Calendar/types'

export type FileCarouselNavigationItem = {
  id: string
  startDate: string | null
}

interface Input {
  filters?: CalendarFilters
  id: string
  onClose: () => void
  showCarousel?: boolean
  startDate: string | null
}

interface Output {
  currentItem: FileCarouselNavigationItem
  displayedFile: AlmanacFileShowItem | null
  handleRemoveCurrentFile: (fileKey?: string, skipNavigation?: boolean) => void
  handleUpdateAlmanacFile: (updatedFile: AlmanacFileShowItem) => void
  isFileFetching: boolean
  isFileLoading: boolean
  nextFile: AlmanacFileShowItem | null
  nextKey: string | null
  previousFile: AlmanacFileShowItem | null
  previousKey: string | null
  setCurrentItem: React.Dispatch<React.SetStateAction<FileCarouselNavigationItem>>
}

export const useContentPreviewFiles = ({ id, startDate, filters, showCarousel = true, onClose }: Input): Output => {
  const workspaceId = useCurrentWorkspaceId()
  const queryClient = useQueryClient()

  const [currentItem, setCurrentItem] = useState<FileCarouselNavigationItem>({ id, startDate })

  const filtersSignature = useMemo(() => (filters ? JSON.stringify(filters) : undefined), [filters])

  useEffect(() => {
    setCurrentItem({ id, startDate })
  }, [id, startDate])

  const primeFileCache = useCallback(
    (file: AlmanacFileShowItem | null, startDateOverride?: string | null) => {
      if (!workspaceId || !file) return
      setFileDetailCache(queryClient, workspaceId, file)

      const normalizedStartDate = startDateOverride ?? null
      if (!filtersSignature && !normalizedStartDate) return

      queryClient.setQueryData(
        [
          ...filesKeys.detail(workspaceId, file.key),
          {
            filtersSignature,
            startDate: normalizedStartDate,
          },
        ],
        file,
      )
    },
    [filtersSignature, queryClient, workspaceId],
  )

  const cachedFilesRef = useRef<Record<string, AlmanacFileShowItem>>({})

  const cacheFile = useCallback((file: AlmanacFileShowItem | null) => {
    if (!file) return
    cachedFilesRef.current[file.key] = file
  }, [])

  const {
    data: currentFileData,
    isLoading: isFileLoading,
    isFetching: isFileFetching,
  } = useFile(currentItem.id, {
    filters,
    startDate: currentItem.startDate ?? undefined,
    cacheTime: 0,
    staleTime: 0,
  })

  const almanacFile = currentFileData ?? null

  useEffect(() => {
    cacheFile(almanacFile)
  }, [almanacFile, cacheFile])

  const cachedCurrentFile = cachedFilesRef.current[currentItem.id] ?? null
  // Always prefer the freshest query result and only fall back to cache when it is unavailable
  const displayedFile = almanacFile ?? cachedCurrentFile

  const lastFileKeyRef = useRef<string | null>(null)
  const persistedPreviousKey = useRef<string | null>(null)
  const persistedNextKey = useRef<string | null>(null)

  useEffect(() => {
    const currentFileKey = displayedFile?.key ?? null
    const currentPrevKey = displayedFile?.previous_file_key ?? null
    const currentNextKey = displayedFile?.next_file_key ?? null

    if (currentFileKey !== lastFileKeyRef.current) {
      // New file: reset persisted keys
      persistedPreviousKey.current = currentPrevKey
      persistedNextKey.current = currentNextKey
      lastFileKeyRef.current = currentFileKey
    } else if (currentPrevKey !== null || currentNextKey !== null) {
      // Same file: update persisted keys if they exist
      if (currentPrevKey !== null) persistedPreviousKey.current = currentPrevKey
      if (currentNextKey !== null) persistedNextKey.current = currentNextKey
    }
  }, [displayedFile?.key, displayedFile?.previous_file_key, displayedFile?.next_file_key])

  const previousKey = displayedFile?.previous_file_key ?? persistedPreviousKey.current ?? null
  const nextKey = displayedFile?.next_file_key ?? persistedNextKey.current ?? null

  const { data: previousFileData } = useFile(previousKey, {
    filters,
    enabled: Boolean(previousKey && showCarousel),
  })
  const { data: nextFileData } = useFile(nextKey, {
    filters,
    enabled: Boolean(nextKey && showCarousel),
  })

  const previousFile = previousFileData || null
  const nextFile = nextFileData || null

  useEffect(() => {
    cacheFile(previousFile)
  }, [cacheFile, previousFile])

  useEffect(() => {
    cacheFile(nextFile)
  }, [cacheFile, nextFile])

  // Batch cache priming to avoid multiple effect runs
  useEffect(() => {
    primeFileCache(displayedFile, currentItem.startDate ?? null)
    primeFileCache(previousFile, previousFile?.post_at ?? null)
    primeFileCache(nextFile, nextFile?.post_at ?? null)
  }, [displayedFile, currentItem.startDate, previousFile, nextFile, primeFileCache])

  const handleUpdateAlmanacFile = useCallback(
    (updatedFile: AlmanacFileShowItem) => {
      if (!workspaceId) return
      if (updatedFile.key === currentItem.id) {
        setCurrentItem((prev) => ({ ...prev, startDate: updatedFile.post_at }))
      }
      setFileDetailCache(queryClient, workspaceId, updatedFile)
      optimisticUpdateFileInInfiniteLists(queryClient, workspaceId, updatedFile.key, (file) => {
        return mergeShowIntoIndexItem(file, updatedFile)
      })
    },
    [currentItem.id, queryClient, workspaceId, setCurrentItem],
  )

  const handleRemoveCurrentFile = useCallback(
    (fileKey?: string, skipNavigation?: boolean) => {
      if (!fileKey) return

      optimisticRemoveFilesFromInfiniteLists(queryClient, workspaceId, [fileKey])

      if (fileKey !== currentItem.id) return

      if (!previousKey && !nextKey) {
        onClose()
        return
      }

      if (skipNavigation) return

      if (previousKey) {
        setCurrentItem({ id: previousKey, startDate: previousFile?.post_at ?? null })
      } else if (nextKey) {
        setCurrentItem({ id: nextKey, startDate: nextFile?.post_at ?? null })
      } else {
        onClose()
      }
    },
    [
      currentItem.id,
      nextFile?.post_at,
      nextKey,
      onClose,
      previousFile?.post_at,
      previousKey,
      queryClient,
      workspaceId,
      setCurrentItem,
    ],
  )

  return {
    currentItem,
    setCurrentItem,
    displayedFile,
    previousFile,
    nextFile,
    previousKey,
    nextKey,
    isFileLoading,
    isFileFetching,
    handleUpdateAlmanacFile,
    handleRemoveCurrentFile,
  }
}

const mergeShowIntoIndexItem = (
  indexFile: AlmanacFileIndexItem,
  showFile: AlmanacFileShowItem,
): AlmanacFileIndexItem => ({
  ...indexFile,
  title: showFile.title ?? null,
  post_at: showFile.post_at ?? null,
  created_at: showFile.created_at,
  updated_at: showFile.updated_at ?? null,
  viewed_at: showFile.viewed_at ?? null,
  post_status: showFile.post_status ?? null,
  asset_type: showFile.asset_type ?? null,
  channel: showFile.channel ?? null,
  image_url: showFile.image_url ?? null,
  duration: showFile.duration,
  post_url: showFile.post_url ?? null,
  image_url_generating: showFile.image_url_generating ?? null,
  scheduled_post_targets: showFile.scheduled_post_targets ?? indexFile.scheduled_post_targets,
  emoji: showFile.emoji ?? null,
  channel_emoji: showFile.channel_emoji ?? null,
  parent_id: showFile.parent_id ?? null,
  external_integration_id: showFile.external_integration_id ?? null,
  view_metadata: showFile.view_metadata ?? null,
  design_content: showFile.design_content ?? null,
  content: showFile.content ?? null,
  width: showFile.width,
  height: showFile.height,
})
